/**
 * QuotePrintView — the customer-facing quote. Port of quote-gen `render.py:render_html`.
 *
 * Layout, wording, table structure and terms bullets are held identical to the shipped
 * PDF (verified against OS-Q-47191888269) so a quote a rep prints here is
 * indistinguishable from one OptiClaw generated. Changing copy here means changing it in
 * render.py too, or the two channels diverge.
 *
 * "Print to PDF" is the browser's own print pipeline — no PDF library. The screen-only
 * chrome carries `print:hidden`; this component is the only thing `@media print` keeps.
 *
 * !!! Only PRICED lines appear !!! An unresolved or unpriced line is deliberately left
 * off the customer's copy and surfaced to the operator as a warning instead — a quote
 * must never show a blank price.
 */
import { formatMoney } from '../pricing/cents';
import type { PricedLine, PricedQuote } from '../pricing/types';
import { LOGO_SVG_B64 } from '../logo-data';
import { defaultTerms, linkifyTerm, printableTerms } from '../terms';
import type { QuoteTerm } from '../terms';

/**
 * Brand teal — the exact value the vendored logo's cloud mark is filled with, so the accent
 * colour and the logo can never drift apart. Deliberately NOT render.py's
 * `OS_TEAL = '#20CE88'`, which is off-brand (hue 148 vs 168). Used by the CSS custom
 * property and the no-logo text fallback. See logo-data.ts for provenance.
 */
const OS_TEAL = '#1AB394';

const COMPANY_NAME = 'OptiSigns.com © OptiSigns Inc.';
const COMPANY_ADDR = '7676 Hillmont Street, Suite 300, Houston, TX 77040, USA';

// Add-ons print in the SUBSCRIPTION table: they are annual recurring licences, and the
// shipped template gives AeriCast / OptiSound their own service descriptions there.
const SUB_KINDS: PricedLine['kind'][] = ['subscription', 'addon'];
const HW_KINDS: PricedLine['kind'][] = ['device', 'shipping'];

/** "June 30, 2026" — no leading zero on the day. */
function formatQuoteDate(d: Date): string {
  return `${d.toLocaleString('en-US', { month: 'long' })} ${d.getDate()}, ${d.getFullYear()}`;
}

function subDescription(plan: string, kind?: PricedLine['kind']): string {
  const p = plan.toLowerCase();
  if (p.includes('aericast')) return 'OptiSigns AeriCast Service';
  if (p.includes('optisound') || p.includes('background music') || p.includes('backgroundmusic')) {
    return 'OptiSigns OptiSound Service';
  }
  // An add-on is a service, not a SaaS plan — "…SaaS Video Wall Plan" would misdescribe it.
  if (kind === 'addon') return `OptiSigns ${plan} Service`;
  return `OptiSigns Digital Signage SaaS ${plan} Plan`;
}

/** Short "Item" column from the full product name: drop the "OptiSigns " prefix. */
function hwItemLabel(name: string): string {
  const n = (name || '').trim();
  return n.toLowerCase().startsWith('optisigns ') ? n.slice('OptiSigns '.length).trim() : n;
}

/** Shipping rows get a cleaned item + a region-qualified description. */
function hwRow(l: PricedLine): { item: string; description: string } {
  if (l.kind === 'shipping') {
    const name = l.label || 'Shipping & Handling';
    const item = name.includes(' & ') ? name.split(' & ')[0] : 'Shipping & Handling';
    const region = l.region === 'international' ? 'International' : 'US';
    return { item, description: `${name} (${region})` };
  }
  return { item: hwItemLabel(l.label), description: l.label };
}

export interface QuotePrintViewProps {
  quote: PricedQuote;
  quoteNumber: string;
  /**
   * The T&C bullets for THIS quote. Defaulted rather than required so the print view can
   * still be rendered standalone and get the standard six.
   */
  terms?: QuoteTerm[];
  /** Injected so the rendered date is deterministic in tests. */
  today?: Date;
}

export function QuotePrintView({
  quote,
  quoteNumber,
  terms,
  today = new Date(),
}: QuotePrintViewProps) {
  const bullets = printableTerms(terms ?? defaultTerms());
  const cur = quote.currency;
  const cust = quote.customer;
  const expires = new Date(today);
  expires.setDate(expires.getDate() + 30);

  const priced = quote.lines.filter((l) => l.amountCents !== null);
  const subs = priced.filter((l) => SUB_KINDS.includes(l.kind));
  const hardware = priced.filter((l) => HW_KINDS.includes(l.kind));

  const subTotal = subs.reduce((s, l) => s + (l.amountCents ?? 0), 0);
  const hwTotal = hardware.reduce((s, l) => s + (l.amountCents ?? 0), 0);
  const discount = quote.discountCents;

  // Summed from the PRICED lines rather than read off quote.totalCents, so an unpriced
  // line can never be silently folded into a printed figure. Equal to `quote.totalCents`
  // whenever the quote is ready to print, which is the only state that reaches paper.
  const grand = subTotal + hwTotal - discount;
  /** What the subscription section actually comes to once each line's discount is off. */
  const netSubTotal = subTotal - discount;



  // Every quote is annual; the column simply states it.
  const paymentLabel = 'Annual';

  return (
    <div className="quote-print" data-testid="quote-print-view">
      {/* Page setup + typography deliberately mirror render.py's inline CSS rather than
          using Tailwind: this block must render identically whether it is on screen or
          in the print pipeline, independent of the app's theme tokens. */}
      <style>{`
        .quote-print {
          --teal: ${OS_TEAL}; --ink: #1f2933; --mut: #6b7280; --head: #42526e; --line: #e5e7eb;
          font: 14px/1.55 -apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif;
          color: var(--ink);
          background: #fff;
          max-width: 8.5in;
          margin: 0 auto;
          padding: 48px 56px;
          box-sizing: border-box;
        }
        .quote-print * { box-sizing: border-box; }
        .quote-print .logo { display: block; margin: 8px auto 28px; width: 300px; max-width: 60%; height: auto; }
        .quote-print .company { font-weight: 700; color: var(--head); line-height: 1.5; }
        .quote-print .date { color: var(--mut); margin-top: 10px; }
        .quote-print .to { font-weight: 700; margin-top: 22px; }
        .quote-print .intro { margin: 18px 0 22px; }
        .quote-print table.q {
          width: 100%; border-collapse: collapse; border: 1px solid var(--line);
          border-radius: 8px; margin: 14px 0; overflow: hidden;
        }
        .quote-print table.q th, .quote-print table.q td {
          padding: 11px 14px; border-bottom: 1px solid var(--line); font-size: 13.5px;
        }
        .quote-print table.q thead th { text-align: left; font-weight: 700; color: var(--ink); }
        .quote-print table.q td.c, .quote-print table.q th.c { text-align: center; }
        .quote-print table.q td.r, .quote-print table.q th.r { text-align: right; }
        .quote-print table.q tfoot td { border-bottom: none; }
        .quote-print .b { font-weight: 700; }
        .quote-print .disc { color: #047857; }
        /* A discount belongs to the line above it, so it reads as part of that row rather
           than as another item: no rule between them, lighter weight, slightly inset. */
        .quote-print table.q tr.discrow td { border-bottom: none; padding-top: 0; font-size: 12.5px; }
        .quote-print table.q tr.discrow + tr td { border-top: 1px solid var(--line); }
        .quote-print .muted { color: var(--mut); }
        .quote-print .small { font-size: 12.5px; }
        .quote-print .total {
          display: flex; justify-content: flex-end; align-items: baseline;
          gap: 36px; margin: 34px 8px 26px 0;
        }
        .quote-print .total .lbl { font-size: 26px; font-weight: 700; }
        .quote-print .total .amt { font-size: 34px; font-weight: 800; letter-spacing: -.5px; }
        .quote-print ul.terms { color: var(--mut); margin: 6px 0; padding-left: 22px; }
        .quote-print ul.terms li { margin: 6px 0; }
        .quote-print ul.terms a { color: var(--teal); text-decoration: none; }
        .quote-print .expires { color: var(--mut); margin-top: 10px; }

        @media print {
          /* margin:0 is load-bearing, not cosmetic. Chrome draws its "Headers and
             footers" (page URL + date + page numbers) INSIDE the @page margin box, and
             that checkbox is ON by default in the print dialog — so a rep's quote would
             carry "localhost:5175" or "staff.optisigns.com" in the margin unless they
             remember to untick it. With no margin box there is nowhere to draw them, so
             they cannot appear regardless of the setting. The page's whitespace moves to
             .quote-print's own padding below.
             CAVEAT: padding applies at the START and END of the block, not per page, so a
             quote that spills onto page 2 has no top inset there. That is the cost of
             guaranteeing no header — see the padding rule for the mitigation. */
          @page { size: Letter; margin: 0; }
          /* The app shell, the form and the operator warnings never reach paper.
             Hiding via visibility rather than display:none on ancestors: the print
             root is nested deep inside the router + shell, so collapsing any ancestor
             would collapse the quote with it. Visibility is inherited but overridable,
             so re-showing the subtree works at any depth. */
          body * { visibility: hidden !important; }
          .quote-print-root, .quote-print-root * { visibility: visible !important; }
          .quote-print-root {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
          }
          /* The page inset now lives here, because @page has no margin (see above).
             0.6in matches the sample quote's measured 0.58in left / 0.62in top.

             box-decoration-break:clone is what makes that safe on a multi-page quote.
             By default padding applies only at the START and END of a fragmented block,
             so page 2 would begin flush to the trim — measured at 0.11in, which is
             effectively no margin. Cloning re-applies the padding to EVERY fragment, so
             each page gets its own inset. Verified: page 1's last ink moved from 0.18in
             above the bottom trim to 1.30in once this was set. */
          .quote-print {
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
            max-width: none;
            margin: 0;
            padding: 0.6in;
          }
          .quote-print table.q { page-break-inside: auto; }
          .quote-print tr { page-break-inside: avoid; }
          /* A table may span pages, but never split a row or the Total figure.
             The terms list is deliberately NOT kept together: holding the whole block
             intact pushed every bullet to page 2 and left page 1 ending at the Total with
             no terms visible at all. Letting it flow puts the terms directly under the
             Total where a reader expects them, spilling over only as far as needed —
             matching the shipped quote. Individual bullets still can't split. */
          .quote-print .total { page-break-inside: avoid; break-inside: avoid; }
          .quote-print ul.terms li { page-break-inside: avoid; break-inside: avoid; }
          /* Keep the on-screen-only quote-number line off the printed page. */
          .quote-print .print\\:hidden { display: none !important; }
        }
      `}</style>

      {LOGO_SVG_B64 ? (
        <img
          className="logo"
          src={`data:image/svg+xml;base64,${LOGO_SVG_B64}`}
          alt="OptiSigns"
        />
      ) : (
        <div style={{ textAlign: 'center', fontSize: 34, fontWeight: 800, color: OS_TEAL }}>
          {'OptiSigns'}
        </div>
      )}

      <div className="company">{COMPANY_NAME}</div>
      <div className="company">{COMPANY_ADDR}</div>
      <div className="date">{`Date: ${formatQuoteDate(today)}`}</div>

      {/* First non-empty of company → name → email. Company wins: that's who is billed. */}
      <div className="to">{`To: ${cust.company || cust.name || cust.email || '—'}`}</div>

      <div className="intro">
        {'Thank you for discussing your Digital Signage needs with us.'}
        <br />
        {'Please find the quotes for our software as a service below.'}
      </div>

      {subs.length > 0 ? (
        <table className="q" data-testid="quote-print-subscriptions">
          <thead>
            <tr>
              <th>{'Plan'}</th>
              <th>{'Description'}</th>
              <th className="c">{'Quantity'}</th>
              <th className="c">{'Payment'}</th>
              <th className="r">{'Price'}</th>
            </tr>
          </thead>
          <tbody>
            {/* Each line shows its LIST price, then its own discount beneath it. A single
                summary discount row cannot represent this any more: 15% off Pro Plus and
                5% off OptiSound net out to a figure that matches neither, and a customer
                checking the quote against what was agreed would find nothing to tie to. */}
            {subs.flatMap((l, i) => {
              const row = (
                <tr key={`${l.sku ?? l.label}-${i}`}>
                  <td>{l.label}</td>
                  <td>{subDescription(l.label, l.kind)}</td>
                  <td className="c">{l.quantity}</td>
                  <td className="c">{paymentLabel}</td>
                  <td className="r">{formatMoney(l.amountCents, cur)}</td>
                </tr>
              );
              if (!l.discountCents) return [row];
              return [
                row,
                <tr key={`${l.sku ?? l.label}-${i}-disc`} className="discrow">
                  <td />
                  <td colSpan={3} className="disc">{`Less ${l.discountPct}% discount`}</td>
                  <td className="r disc">{`-${formatMoney(l.discountCents, cur)}`}</td>
                </tr>,
              ];
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="r b">
                {'Annual Subscription Cost'}
              </td>
              <td className="r b" data-testid="quote-print-subtotal">
                {formatMoney(netSubTotal, cur)}
              </td>
            </tr>
          </tfoot>
        </table>
      ) : null}

      {hardware.length > 0 ? (
        <table className="q" data-testid="quote-print-hardware">
          <thead>
            <tr>
              <th>{'Item'}</th>
              <th>{'Description'}</th>
              <th className="c">{'Quantity'}</th>
              <th className="c">{'Payment'}</th>
              <th className="r">{'Price'}</th>
            </tr>
          </thead>
          <tbody>
            {hardware.map((l, i) => {
              const { item, description } = hwRow(l);
              return (
                <tr key={`${l.sku ?? l.label}-${i}`}>
                  <td>{item}</td>
                  <td>{description}</td>
                  <td className="c">{l.quantity}</td>
                  <td className="c">{'One-Time'}</td>
                  <td className="r">{formatMoney(l.amountCents, cur)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="r b">
                {'Total Hardware Cost'}
              </td>
              <td className="r b">{formatMoney(hwTotal, cur)}</td>
            </tr>
            <tr>
              <td colSpan={5} className="c muted small">
                {'Hardware Purchase - No Recurring Costs'}
              </td>
            </tr>
          </tfoot>
        </table>
      ) : null}

      <div className="total">
        <span className="lbl">{'Total'}</span>
        <span className="amt" data-testid="quote-print-total">
          {formatMoney(grand, cur)}
        </span>
      </div>

      {/* Terms + expiry. Deliberately allowed to flow across a page break — see the
          print CSS above for why keeping them together was worse. */}
      <div className="closing">
        {/* Rendered from the quote's own list, so a rep can drop a bullet that does not
            apply or add one that was negotiated. A URL in the text is still linked — the
            anchor is derived from the text rather than hardcoded beside it, so editing the
            line cannot silently flatten the link. */}
        {bullets.length > 0 ? (
          <ul className="terms" data-testid="quote-print-terms">
            {bullets.map((t) => (
              <li key={t.id}>
                {linkifyTerm(t.text).map((part, i) =>
                  part.href ? (
                    <a key={i} href={part.href}>
                      {part.text}
                    </a>
                  ) : (
                    <span key={i}>{part.text}</span>
                  ),
                )}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="expires">{`Quote Expires: ${formatQuoteDate(expires)}`}</div>
      </div>

      {/* Screen-only: the quote number is metadata for the rep, not part of the template. */}
      <div className="muted small print:hidden" style={{ marginTop: 18 }}>
        {`Quote ${quoteNumber}`}
      </div>
    </div>
  );
}
